<i18n>
{
  "en": {
    "title": "EcoStruxure, R&D, IoT Digital Sustainability 2017-2018",
    "description": "Taking action to fight climate change and protect the environment, is the only path to a green, sustainable world. Discover how we use EcoStruxure<sup>™</sup> solutions, digital innovation, and open collaboration to create efficient, effective, green energy products and solutions for our customers.",
    "sustainable": {
      "section": "",
      "title": "Sustainable Solutions",
      "overview": {
        "vid": {
          "title": "Leading with a Sustainable Strategy",
          "description": "Emmanuel Lagarrigue,<br> Executive Vice President, Strategy, Schneider Electric"
        },
        "dataDriven": {
          "title": "Data-driven sustainability",
          "para": "If the world is going to become more sustainable, we all need to know where we stand — how much energy we’re using and how to use energy more intelligently. The convergence of the Internet of Things (IoT) and energy gives us a tremendous opportunity to tap the value of data — especially data in context — to advance sustainability across buildings, data centers, industry, and infrastructure.",
          "paraB": "Just as oil has delivered the economy of today, data is driving the sustainable, low-carbon economy of tomorrow. Our solutions and services are all focused on this common purpose: using the IoT to capture data and turn it into meaningful, profitable, sustainable action for our customers.",
          "paraC": "Schneider Electric brings together energy management, automation, and software in one interoperable IoT architecture: EcoStruxure™. We’re uniquely positioned to meet the demand for more efficient, connected, and smarter energy through the IoT. We also partner with clients to help them buy energy more strategically, reduce energy use, and operate more sustainably through our EcoStruxure Energy & Sustainability Services.",
          "paraD": "With EcoStruxure Energy & Sustainability Services, we empower our customers’ bold ideas — to both achieve their sustainability goals and compete in today’s digital economy."
        },
        "table1": {
          "row1": {
            "cell1": "90%",
            "cell2": "of the data in the world today were created over the past two years.[i]"
          },
          "references": {
            "txt":"<sup>1</sup> IEA, Digitization & Energy 2017 <a class='t-grey' href='http://www.iea.org/publications/freepublications/publication/DigitalizationandEnergy3.pdf' target='_blank'>http://www.iea.org/publications/freepublications/publication/DigitalizationandEnergy3.pdf</a>"
          }
        }
      }
    }
  },
  "fr": {
    "title": "EcoStruxure, R&D, IoT Digital Sustainability 2017-2018",
    "description": "Taking action to fight climate change and protect the environment, is the only path to a green, sustainable world. Discover how we use EcoStruxure<sup>™</sup> solutions, digital innovation, and open collaboration to create efficient, effective, green energy products and solutions for our customers.",
    "sustainable": {
      "section": "",
      "title": "Solutions Durables",
      "overview": {
        "vid": {
          "title": "Diriger avec une Stratégie Durable",
          "description": "Emmanuel Lagarrigue,<br> Directeur général Stratégie, Schneider Electric"
        },
        "dataDriven": {
          "title": "Les données au service du développement durable",
          "para": "Pour que le monde devienne plus durable, il convient d’avoir les idées claires, de savoir combien d'énergie nous utilisons et comment en faire meilleur usage. La convergence entre Internet des Objets (IdO) et énergie nous donne l’occasion inespérée de valoriser les données &mdash; et notamment les données en contexte &mdash; pour faire progresser le développement durable dans les bâtiments, les centres de données, l’industrie et les réseaux.",
          "paraB": "Tout comme le pétrole a façonné l’économie d'aujourd'hui, les données feront émerger l’économie de demain, qui sera durable et bas carbone. Toutes nos solutions et tous nos services sont pensés en fonction de ce but commun : utiliser l’IdO pour collecter des données et les convertir en actions engagées, rentables et durables, au bénéfice de nos clients.",
          "paraC": "Schneider Electric conjugue gestion de l’énergie, automatisation et logiciels en une architecture IdO unique et interopérable : EcoStruxure<sup>TM</sup>. Nous bénéficions d’un positionnement unique pour répondre à la demande d’une énergie plus efficace et plus connectée grâce à l’Internet des Objets. Par l’intermédiaire de notre entité Energy & Sustainability Services, nous formons des partenariats avec nos clients pour les aider à se montrer plus stratégiques dans l’achat d’énergie, à réduire leur consommation et à opérer plus durablement.",
          "paraD": "Avec EcoStruxure et l'activité Energy & Sustainability Services, nous donnons à nos clients les moyens de concrétiser leurs idées les plus ambitieuses, tant pour atteindre leurs objectifs de développement durable que pour se montrer compétitifs à l’ère de l’économie numérique."
        },
        "table1": {
          "row1": {
            "cell1": "90%",
            "cell2": "des données qui existent aujourd'hui dans le monde ont été générées ces deux dernières années. [1]"
          },
          "references": {
            "txt":"<sup>1</sup> IEA, Digitization & Energy 2017 <a class='t-grey' href='http://www.iea.org/publications/freepublications/publication/DigitalizationandEnergy3.pdf' target='_blank'>http://www.iea.org/publications/freepublications/publication/DigitalizationandEnergy3.pdf</a>"
          }
        }
      }
    }
  }
}
</i18n>

<template>
  <div id="">
    <Hero :loResSrc="require('@/assets/img/homepage/sustainable-low.jpg')" :hiResSrc="require('@/assets/img/homepage/sustainable.jpg')" chapter="02" :title="$t('sustainable.title')"/>

    <section class="m-b-120 m-t-120">
      <YouTubeVideo videoId="goSMxfkEWxQ" :title="$t('sustainable.overview.vid.title')" :description="$t('sustainable.overview.vid.description')" :preview="require('@/assets/img/thumbnails/SSDHighlightVideo_Emmanuel_Still.jpg')" />
    </section>

    <section class="m-b-120 m-t-120">
      <TxtMedia class="m-b-50">
        <h2 id="datadrivensustainability" class="m-b-35" slot="main-content" v-html="$t('sustainable.overview.dataDriven.title')"/>
        <p slot="main-content" v-html="$t('sustainable.overview.dataDriven.para')"/>
        <p slot="main-content" v-html="$t('sustainable.overview.dataDriven.paraB')"/>
        <p slot="main-content" v-html="$t('sustainable.overview.dataDriven.paraC')"/>
        <p slot="main-content" v-html="$t('sustainable.overview.dataDriven.paraD')"/>
        <div slot="sidebar" class="l-mw-360 l-center">
          <SvgContainer v-if="$i18n.locale === 'en'">
            <SSOverviewIG class="scaling-svg" />
          </SvgContainer>
          <SvgContainer v-if="$i18n.locale === 'fr'">
            <SSOverviewIGFR class="scaling-svg" />
          </SvgContainer>
        </div>
      </TxtMedia>
    </section>

    <section class="m-t-120 m-b-50 l-section-pad l-bg-white">
      <div class="l-content">
        <div class="row">
          <div class="col-xs-14 col-xs-offset-2">
            <SvgContainer v-if="$i18n.locale === 'en'" slot="sidebar">
              <SSOverviewIG2 class="scaling-svg" />
            </SvgContainer>
            <SvgContainer v-if="$i18n.locale === 'fr'">
              <SSOverviewIG2FR class="scaling-svg" />
            </SvgContainer>
          </div>
        </div>
      </div>
    </section>

    <References>
      <Reference v-html="$t('sustainable.overview.table1.references.txt')" />
    </References>

    <FooterNav nextRoute="SustainableSolutions_Ecostruxure" prevRoute="Introduction_EnvironmentalStrategy" />

  </div>
</template>

<script>
import FooterNav from '@/components/FooterNav'
import Hero from '@/components/Hero'
import YouTubeVideo from '@/components/YouTubeVideo'
import Quote from '@/components/Quote'
import Txt from '@/components/Txt'
import TxtMedia from '@/components/TxtMedia'
import Statement from '@/components/Statement'
import FourCols from '@/components/FourCols'
import FourColsItem from '@/components/FourColsItem'
import CTA from '@/components/CTA'
import Table from '@/components/Table'
import TableRow from '@/components/TableRow'
import TableCell from '@/components/TableCell'
import References from '@/components/References'
import Reference from '@/components/Reference'
import SvgContainer from '@/components/SvgContainer'

import SSOverviewIG from '@/assets/img/ig/sustainable-solutions-overview-data-driven-sustainability.svg'
import SSOverviewIGFR from '@/assets/img/ig/sustainable-solutions-overview-data-driven-sustainability_fr.svg'
import SSOverviewIG2 from '@/assets/img/ig/sustainable-solutions-overview-worlds-data.svg'
import SSOverviewIG2FR from '@/assets/img/ig/sustainable-solutions-overview-worlds-data_french.svg'

export default {
  name: 'SustainableSolutions_Overview',
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  mounted () {
    if (window.location.hash.substr(1).length > 0) {
      this.$nextTick(function () {
        document.getElementById(window.location.hash.substr(1)).scrollIntoView()
      })
    }
  },
  components: {
    FooterNav,
    Hero,
    YouTubeVideo,
    Quote,
    Txt,
    Statement,
    FourCols,
    FourColsItem,
    CTA,
    TxtMedia,
    References,
    Reference,
    Table,
    TableRow,
    TableCell,
    SSOverviewIG,
    SSOverviewIGFR,
    SSOverviewIG2,
    SSOverviewIG2FR,
    SvgContainer
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/sustainable-solutions/overview.vue